import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { Button, Flex, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, VStack } from '@chakra-ui/react'
import { PROJECT_TASK_TEMPLATE_TYPES, ROLES } from 'lib'
import { UiSelect } from 'components/common'
import { createProjectSectionTask, deleteProjectSectionTask, getProjectExtraDetailByProjectId, getProjectTemplateSectionTasksByTemplateType, getUsersByRoles, updateProjectSectionTask } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { useImmer } from 'use-immer'
import { format as formatDate } from 'date-fns'

const defaultTemplateTask = { uid: 'Custom', title: 'Custom' }

const initialForm = {
  templateTask: defaultTemplateTask,
  title: 'Custom',
  assignedUserId: 0,
  dueDate: null
}

export const ModalTaskType = {
  Add: 'Add',
  Edit: 'Edit'
}

export const ModalTask = ({ modalType, isOpen, onClose, projectId, task, reload }) => {
  const { createToast } = useAppContext()
  const [form, setForm] = useImmer(initialForm)
  const [isLocked, setIsLocked] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [templateTasks, setTemplateTasks] = useState([])

  useEffect(() => {
    const load = async () => {
      try {
        const projectExtraDetail = await getProjectExtraDetailByProjectId(projectId)
        let installationParty = PROJECT_TASK_TEMPLATE_TYPES.General
        if (projectExtraDetail?.installationOption === 'T.B.D' || projectExtraDetail?.installationOption === 'Client Installation' || projectExtraDetail?.installationOption === 'Client Installation Anchor Bolt Kit / Guide' || projectExtraDetail?.installationOption === 'Self Install/Client Install') {
          installationParty = PROJECT_TASK_TEMPLATE_TYPES.Client
        } else {
          installationParty = PROJECT_TASK_TEMPLATE_TYPES.General
        }
        const { rows } = await getProjectTemplateSectionTasksByTemplateType(installationParty)
        setTemplateTasks([defaultTemplateTask, ...rows])
      } catch (err) {
        // err process
      }
    }
    load()
  }, [])

  useEffect(() => {
    if (task) {
      const templateTask = templateTasks.filter(v => v.uid === task?.templateTaskUid)?.[0] || defaultTemplateTask
      const dueDate = new Date(task?.dueDate)
      setForm({
        templateTask,
        title: task?.title || '',
        assignedUser: { id: task?.assignedUserId ?? 0, name: task?.assignedUserName ?? '' },
        dueDate: task?.dueDate ? formatDate(new Date(dueDate.getTime() + dueDate.getTimezoneOffset() * 60000), 'yyyy-MM-dd') : null
      })
    } else {
      setForm(initialForm)
    }
  }, [isOpen, task, templateTasks])

  const onCancel = () => {
    onClose()
  }

  const handleChange = (e) => {
    setForm(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const { assignedUser, templateTask, ...rest } = form

    if (!templateTask?.uid) {
      createToast('error', 'Error', 'Select template task.')
      return
    }
    if (!form.title) {
      createToast('error', 'Error', 'Input title.')
      return
    }

    if (modalType === ModalTaskType.Add) {
      handleAddTask({ sectionId: task.sectionId, ...rest, assignedUserId: assignedUser?.id || 0, templateTaskUid: templateTask.uid })
    } else {
      handleEditTask({ id: task.id, sectionId: task.sectionId, ...rest, assignedUserId: assignedUser?.id || 0, templateTaskUid: templateTask.uid, sort: task.sort })
    }
  }

  const handleAddTask = async (data) => {
    setIsSubmitting(true)
    try {
      await createProjectSectionTask({
        sectionId: data.sectionId,
        templateTaskUid: data.templateTaskUid,
        title: data.title,
        assignedUserId: data.assignedUserId,
        dueDate: data.dueDate
      })
      createToast('success', 'Success', 'Task added.')
      setIsSubmitting(false)
      onClose()
      reload()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  const handleEditTask = async (data) => {
    setIsSubmitting(true)
    try {
      await updateProjectSectionTask(data.id, {
        sectionId: data.sectionId,
        templateTaskUid: data.templateTaskUid,
        title: data.title,
        assignedUserId: data.assignedUserId,
        dueDate: data.dueDate,
        sort: data.sort
      })
      createToast('success', 'Success', 'Task updated.')
      setIsSubmitting(false)
      onClose()
      reload()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Modal size="xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSubmit}>
            <ModalHeader>{modalType === ModalTaskType.Add ? 'Add' : 'Edit'} Task</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack gap={2} alignItems="none">
                <FormControl isRequired>
                  <FormLabel>Template Task</FormLabel>
                  <UiSelect
                    value={form.templateTask}
                    onChange={v => {
                      setForm(draft => {
                        draft.templateTask = { ...v }
                        draft.title = v?.title
                        setIsLocked(true)
                      })
                    }}
                    loadData={(page, search) => {
                      return { rows: page === 1 ? templateTasks.filter(v => v.title.includes(search)) : [] }
                    }}
                    apiRespProp="rows"
                    getOptionValue={v => v.uid}
                    getOptionLabel={v => v.title}
                    placeholder="Search"
                    noOptionsMessage={() => 'No Result Found'}
                    required={true}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Title</FormLabel>
                  <Flex gap={2}>
                    <Input type="text" name="title" value={form.title} onChange={handleChange} isReadOnly={isLocked} />
                    <Button size="sm" onClick={() => setIsLocked(!isLocked)}>{isLocked ? 'Unlock' : 'Lock'}</Button>
                  </Flex>
                </FormControl>
                <FormControl mt={2}>
                  <FormLabel>Assign to</FormLabel>
                  <UiSelect
                    value={form.assignedUser}
                    onChange={v => {
                      setForm(draft => {
                        draft.assignedUser = { ...v }
                      })
                    }}
                    loadData={(page, search) => getUsersByRoles(page, 20, search, 'name', 'asc', [ROLES.ProjectManager, ROLES.Admin, ROLES.SalesUserProjectManager, ROLES.SalesUser])}
                    apiRespProp="rows"
                    getOptionValue={v => v.id}
                    getOptionLabel={v => v.name}
                    placeholder="Search Users"
                    noOptionsMessage={() => 'No Users Found'}
                    required={true}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Due Date</FormLabel>
                  <Input type="date" name="dueDate" value={form.dueDate ?? ''} onChange={handleChange} />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Flex justifyContent="flex-end" columnGap={2}>
                {modalType === ModalTaskType.Add && (
                  <Button type="submit" size="sm" colorScheme="blue" isLoading={isSubmitting}>Add</Button>
                )}
                {modalType === ModalTaskType.Edit && (
                  <Button type="submit" size="sm" colorScheme="blue" isLoading={isSubmitting}>Save</Button>
                )}
                <Button size="sm" colorScheme="gray" onClick={onCancel}>Cancel</Button>
              </Flex>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  )
}

ModalTask.propTypes = {
  modalType: PropTypes.oneOf(Object.values(ModalTaskType)).isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  projectId: PropTypes.number,
  task: function (props, propName, componentName) {
    if (props.modalType === ModalTaskType.Edit) {
      if (!props[propName]) {
        return new Error(
          `The prop \`${propName}\` is required when modalType is ${ModalTaskType.Edit} in ${componentName}.`
        )
      }
    }
  },
  reload: PropTypes.func
}

export const ModalRemoveTask = ({ task, isOpen, onClose, reload }) => {
  const { createToast } = useAppContext()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleRemoveTask = async (id) => {
    setIsSubmitting(true)
    try {
      await deleteProjectSectionTask(id)
      createToast('success', 'Success', 'Task removed.')
      setIsSubmitting(false)
      onClose()
      reload()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Remove Task</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>Are you sure to remove the task - {task?.title}?</Text>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme='red' mr={3} onClick={() => handleRemoveTask(task?.id)} isLoading={isSubmitting}>
            Remove
          </Button>
          <Button variant='ghost' onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

ModalRemoveTask.propTypes = {
  task: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  reload: PropTypes.func
}
